import React, { FC, useMemo, ReactElement, useState, useEffect } from 'react';
import { SelectInput, BaseProps } from '../Select';

export type SelectAutocompleteProps = BaseProps & {
  variant: 'selectAutocomplete';
  onFocus?: () => void;
  onChange: (value: string) => void;
  optionsSortFn?: (a: string, b: string) => number;
  displayAsEmpty?: boolean;
};

export const SelectAutocomplete: FC<SelectAutocompleteProps> = ({
  value,
  variant,
  options,
  className,
  onFocus,
  onBlur,
  onChange,
  optionsSortFn,
  displayAsEmpty,
  ...props
}) => {
  const [searchString, changeSearchString] = useState<string>('');
  const [hasFocus, changeHasFocus] = useState<boolean>(false);

  const currentFilteredOptions = useMemo(() => {
    const filteredOptions = Object.entries(options).reduce<{
      [k: string]: string | ReactElement;
    }>((acc, [optionValue, label]) => {
      const inputMatchesOptions =
        searchString &&
        typeof label === 'string' &&
        label.toLowerCase().startsWith(searchString.toLowerCase());

      if (inputMatchesOptions) {
        return {
          ...acc,
          [optionValue]: label,
        };
      }
      return acc;
    }, {});

    return searchString && Object.keys(filteredOptions).length
      ? filteredOptions
      : options;
  }, [options, searchString]);

  const fireOnChangeIfValidOptionSelected = (newValue: string) => {
    if (options.hasOwnProperty(newValue)) {
      onChange(newValue);
    }
  };

  useEffect(() => {
    // Allows display to clear if value is cleared
    if (value === '') {
      changeSearchString('');
    }
  }, [value]);

  return (
    <SelectInput
      {...props}
      variant={variant}
      value={displayAsEmpty ? '' : hasFocus ? searchString : value}
      options={currentFilteredOptions}
      className={className}
      optionsSortFn={optionsSortFn}
      onFocus={() => {
        changeHasFocus(true);
        onFocus && onFocus();
      }}
      onChange={inputValue => {
        changeSearchString(inputValue);
        if (inputValue && inputValue !== value) {
          fireOnChangeIfValidOptionSelected(inputValue);
        }
      }}
      onBlur={event => {
        if (searchString && searchString !== value) {
          const key = Object.keys(options).find(key => {
            const label = options[key];
            return typeof label === 'string' && label === searchString;
          });

          key && onChange(key);
        }

        changeSearchString('');
        changeHasFocus(false);
        onBlur && onBlur(event);
      }}
    />
  );
};
